/**
 * 서버 구성 V2 — 딜 서버 서브트리 API (2026-07-30).
 *
 * 서버 구성 편집기(별탭)의 '완료'와 딜 상세 서버 ⋯메뉴가 호출한다.
 * 모든 응답은 그 서버의 서브트리(헤더행 + 자식행)를 돌려준다.
 *
 * 낙관적 잠금: expectedVersion(편집 시작 시점 deal.version) 불일치 시 409
 *   → ApiConflictError. 호출자가 병합/재조회 UX 로 분기한다.
 */

import { z } from 'zod';
import { API_BASE, authHeaders, parseResponse, ApiConflictError, httpError, errorDetail } from './http';
import { DealItemSchema, type DealItem } from './schemas';

const ServerItemsResponseSchema = z.object({ items: z.array(DealItemSchema) });

// ─── 후보 장바구니 ───────────────────────────────────────────────────────────
/**
 * GET /server-config/candidates?chassis_part=… — 이 Chassis 의 후보 장바구니.
 *
 * 원천은 `chassis_candidate_part`(마이그 151) — 사람이 담은 것이다.
 * QuickSpec 호환 목록(`chassis_compatible_parts`)은 여기에 직접 나오지 않는다.
 * 그건 추천이고, DL380 Gen10 CPU 만 321건이라 화면에 뿌릴 수 있는 것이 아니다.
 * 추천 여부는 `is_recommended` 배지로만 남는다.
 *
 * 담긴 것이 없는 카테고리는 groups 에 오지 않는다. 화면은 CHASSIS 를 제외한
 * 17개 그룹을 항상 그리고, 응답에 없는 카테고리를 0건으로 채운다 —
 * 후보 관리 진입점이 그룹 헤더의 버튼이기 때문.
 */
const CandidateItemSchema = z.object({
  option_part:     z.string(),
  description_raw: z.string().nullable(),
  /** 카탈로그 출처 테이블명 — 담을 때 약식 재계산 키로 넘긴다. */
  catalog_source:  z.string().nullable(),
  /** QuickSpec 호환 목록에도 있는가. 후보 자격과는 무관한 표시용 플래그. */
  is_recommended:  z.boolean(),
  min_qty:         z.number().nullable(),
  max_qty:         z.number().nullable(),
  /** 카탈로그에서 찾지 못함 — 담은 뒤 카탈로그가 바뀐 경우. */
  is_missing:      z.boolean(),
});
const CandidatesResponseSchema = z.object({
  chassis: z.object({
    option_part:     z.string(),
    vendor:          z.string().nullable(),
    model:           z.string().nullable(),
    sku_type:        z.string().nullable(),
    generation:      z.string().nullable(),
    rack_unit:       z.number().nullable(),
    description_raw: z.string().nullable(),
  }),
  /**
   * 후보를 공유하는 SKU 묶음(마이그 172). 단독이거나 미편성이면 null.
   *
   * ★ 여기 안 적으면 zod 가 모르는 키라고 조용히 버린다. 백엔드가 보내도
   *   화면까지 절대 안 온다 — quality_note 때 똑같이 밟았던 자리다.
   * ★ `.default(null)` 인 이유: 프론트가 먼저 배포되고 백엔드(172 대응)가
   *   아직이면 이 키가 아예 없다. 없으면 파싱 실패가 아니라 "그룹 없음"으로
   *   떨어져야 한다.
   */
  candidate_group: z.object({
    label:     z.string(),
    sku_count: z.number(),
    members:   z.array(z.string()),
    split_key: z.string().nullable(),
  }).nullable().default(null),
  groups: z.array(z.object({
    spec_category: z.string(),
    count:         z.number(),
    items:         z.array(CandidateItemSchema),
  })),
});

export type CandidateItem       = z.infer<typeof CandidateItemSchema>;
export type CandidatesResponse  = z.infer<typeof CandidatesResponseSchema>;

/**
 * GET + zod 검증 공통부.
 *
 * ★ 제네릭을 `T extends z.ZodTypeAny` 로 두면 안 된다 — parseResponse 는
 *   `<T>(data: unknown, schema: ZodType<T>, ctx): T` 라 출력 타입을 추론하지
 *   못하고 unknown 이 된다. 출력 타입을 직접 제네릭으로 받는다.
 */
async function getJson<T>(
  path:   string,
  schema: z.ZodType<T>,
  label:  string,
  opts:   { signal?: AbortSignal } = {},
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders(), ...opts });
  if (!res.ok) {
    throw await httpError(res);
  }
  return parseResponse(await res.json(), schema, label);
}

export function fetchCandidates(
  chassisPart: string,
  opts: { signal?: AbortSignal } = {},
): Promise<CandidatesResponse> {
  const qs = new URLSearchParams({ chassis_part: chassisPart }).toString();
  return getJson(
    `/api/admin/server-config/candidates?${qs}`,
    CandidatesResponseSchema, 'GET /server-config/candidates', opts,
  );
}

// ─── 후보 관리 모달 ──────────────────────────────────────────────────────────
/**
 * GET /server-config/candidate-pool — 그 카테고리의 카탈로그 **전량**.
 *
 * 서버가 필터링하지 않는 이유: 검색어를 바꿔도 숨겨진 행의 체크를 잃지 않아야
 * 하고(핸드오프 §4), 그러려면 전량을 손에 들고 필터링해야 한다.
 * `checked` = 이미 담긴 후보. 추천(`recommended`)은 배지·정렬용이며 체크와
 * 무관하다 — 사람이 담지 않은 것은 후보가 아니다.
 */
const CandidatePoolResponseSchema = z.object({
  spec_category: z.string(),
  truncated:     z.boolean(),
  items: z.array(z.object({
    option_part:     z.string(),
    description_raw: z.string().nullable(),
    catalog_source:  z.string(),
    checked:         z.boolean(),
    recommended:     z.boolean(),
  })),
});

export type CandidatePoolResponse = z.infer<typeof CandidatePoolResponseSchema>;
export type CandidatePoolItem     = CandidatePoolResponse['items'][number];

export function fetchCandidatePool(
  chassisPart:  string,
  specCategory: string,
  opts: { signal?: AbortSignal } = {},
): Promise<CandidatePoolResponse> {
  const qs = new URLSearchParams({
    chassis_part: chassisPart, spec_category: specCategory,
  }).toString();
  return getJson(
    `/api/admin/server-config/candidate-pool?${qs}`,
    CandidatePoolResponseSchema, 'GET /server-config/candidate-pool', opts,
  );
}

/**
 * PUT /server-config/candidate-set — 후보 집합 저장.
 * 체크 집합이 곧 장바구니다(선언적 동기화). 해제는 삭제이고, 빈 배열은
 * "이 카테고리 후보를 비운다"는 유효한 저장이다.
 */
export async function saveCandidateSet(
  chassisPart:  string,
  specCategory: string,
  optionParts:  readonly string[],
): Promise<CandidatesResponse> {
  const res = await fetch(`${API_BASE}/api/admin/server-config/candidate-set`, {
    method:  'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body:    JSON.stringify({
      chassis_part: chassisPart, spec_category: specCategory, option_parts: optionParts,
    }),
  });
  if (!res.ok) {
    throw await httpError(res);
  }
  return parseResponse(await res.json(), CandidatesResponseSchema, 'PUT /server-config/candidate-set');
}

// ─── 소스 후보 (재고 + RFQ) ──────────────────────────────────────────────────
/**
 * GET /server-config/sources?parts=A,B,C&deal_id=X — 파트별 소스 후보.
 *
 * 기존 /rfq/deal-sources 를 쓰지 않는 이유: 그 API 는 파트 목록을 deal_item 에서
 * 뽑아, 편집기에서 아직 저장하지 않은 구성품(완료 전)의 RFQ 를 못 찾는다.
 * 원가가 없으면 null 로 온다(0 치환 금지 → '확인 필요' 표시 근거).
 */
const StockCandidateSchema = z.object({
  /** inventory_stock.id — 소스 select value 이자 저장되는 source_ref_id. */
  stock_id:        z.string(),
  option_part:     z.string(),
  condition:       z.string(),
  condition_group: z.string(),
  warehouse:       z.string(),
  qty_available:   z.number(),
  cost_amount:     z.number().nullable(),
  cost_currency:   z.string().nullable(),
  /**
   * 물건의 상태(마이그 170). '' = 정상, DEFECT / NO_MAINBOARD 등.
   * ★ 여기 없으면 zod 가 unknown key 를 조용히 걷어내 화면까지 도달하지 않는다.
   * 값 목록을 enum 으로 고정하지 않는다 — 관측될 때마다 늘어나는 자유 어휘라
   * enum 으로 막으면 새 상태가 들어온 날 응답 검증이 통째로 실패한다.
   */
  quality_note:    z.string().default(''),
});
const RfqCandidateSchema = z.object({
  source_type:    z.enum(['deal_rfq', 'rfq_history']),
  rfq_id:         z.string(),
  option_part:    z.string().nullable(),
  is_alternative: z.boolean(),
  supplier_name:  z.string(),
  price_amount:   z.number().nullable(),
  price_currency: z.string().nullable(),
  condition:      z.string().nullable(),
  status:         z.string(),
  rfq_date:       z.string(),
});
const SourcesResponseSchema = z.object({
  stock: z.array(StockCandidateSchema),
  rfq:   z.array(RfqCandidateSchema),
});

export type StockCandidate  = z.infer<typeof StockCandidateSchema>;
export type RfqCandidate    = z.infer<typeof RfqCandidateSchema>;
export type SourcesResponse = z.infer<typeof SourcesResponseSchema>;

export async function fetchSourceCandidates(
  parts:  readonly string[],
  dealId: string,
): Promise<SourcesResponse> {
  const list = [...new Set(parts.filter(Boolean))];
  if (list.length === 0) return { stock: [], rfq: [] };
  const qs = new URLSearchParams({ parts: list.join(','), deal_id: dealId }).toString();
  const res = await fetch(
    `${API_BASE}/api/admin/server-config/sources?${qs}`,
    { headers: authHeaders() },
  );
  if (!res.ok) {
    throw await httpError(res);
  }
  return parseResponse(await res.json(), SourcesResponseSchema, 'GET /server-config/sources');
}

/** 소스 자유 검색(대체품) — 파트넘버 부분일치로 재고·RFQ 를 훑는다. 빈 검색어면 빈 응답. */
export async function searchSourceCandidates(
  query:  string,
  dealId: string,
): Promise<SourcesResponse> {
  const term = query.trim();
  if (term === '') return { stock: [], rfq: [] };
  const qs = new URLSearchParams({ q: term, deal_id: dealId }).toString();
  const res = await fetch(
    `${API_BASE}/api/admin/server-config/sources?${qs}`,
    { headers: authHeaders() },
  );
  if (!res.ok) {
    throw await httpError(res);
  }
  return parseResponse(await res.json(), SourcesResponseSchema, 'GET /server-config/sources?q');
}

// ─── 저장된 서버 구성 (불러오기 모달) ────────────────────────────────────────
/**
 * GET /server-config/saved-servers — 다른 딜에 저장된 서버 구성 목록(최근 순).
 * `exclude_deal_id` 로 현재 딜을 뺀다 — 같은 딜 안에서는 서버 ⋯ 의 '복제'가 있다.
 */
const SavedServerSchema = z.object({
  server_item_id:   z.string(),
  deal_id:          z.string(),
  deal_no:          z.string().nullable(),
  deal_name:        z.string().nullable(),
  /** 구성명의 거래처 자리(2026-08-14). 목록은 딜명 대신 이걸 보여준다. */
  customer_name:    z.string().nullable().default(null),
  config_no:        z.number().nullable(),
  server_condition: z.string().nullable(),
  server_quantity:  z.number(),
  /** 약식 요약(첫 줄 = 섀시 모델). 모델 폴백에 쓴다. */
  item_description: z.string().nullable(),
  /** `chassis.model` 원본(`DL380p Gen8`). null 이면 약식 첫 줄로 폴백. */
  chassis_model:    z.string().nullable().default(null),
  child_count:      z.number(),
  /** 구성 '완료' 시각 — 완료 버튼이 서버 헤더행을 INSERT 한 순간. */
  created_at:       z.string(),
});
const SavedServersResponseSchema = z.object({
  items:    z.array(SavedServerSchema),
  has_more: z.boolean(),
});

export type SavedServerItem     = z.infer<typeof SavedServerSchema>;
export type SavedServersPage    = z.infer<typeof SavedServersResponseSchema>;

/** 한 번에 보여줄 건수 — 목록이 길어 스크롤만 늘어나므로 끊어 보여준다. */
export const SAVED_SERVER_PAGE = 10;

export function fetchSavedServers(
  query:         string,
  excludeDealId: string,
  offset = 0,
): Promise<SavedServersPage> {
  const qs = new URLSearchParams({
    q: query,
    exclude_deal_id: String(excludeDealId),
    limit:  String(SAVED_SERVER_PAGE),
    offset: String(offset),
  }).toString();
  // 이 파일의 로컬 getJson 은 (path, schema, label) 시그니처다 — http.ts 의
  // getJson(path, opts) 와 다르다. 헷갈리기 쉬우니 인자 위치를 확인할 것.
  return getJson(
    `/api/admin/server-config/saved-servers?${qs}`,
    SavedServersResponseSchema, 'GET /server-config/saved-servers',
  );
}

/**
 * POST /deal/:dealId/server/import — 다른 딜의 서버 구성을 이 구성으로 복사.
 * 구성(무엇을 몇 개)만 가져오고 소스·원가·판매가·번들가는 승계하지 않는다.
 */
export async function importDealServer(
  dealId:           string,
  input:            { configuration_id: string; source_server_item_id: string },
  expectedVersion?: number,
): Promise<DealItem[]> {
  return sendServerCommand(
    `${API_BASE}/api/admin/deal/${encodeURIComponent(dealId)}/server/import`,
    'POST',
    { ...input, expected_version: expectedVersion },
    'POST /deal/:id/server/import',
  );
}

// ─── 기본 구성 (chassis_base_parts) ──────────────────────────────────────────
/**
 * 섀시 모델별 재사용 구성. 후보(장바구니)와 다르다 —
 * 후보는 "고를 거리", 기본 구성은 "자동으로 넣을 확정 세트 + 수량"이다.
 * 소스/원가/판매가는 담지 않는다(시간가변 상거래 값은 딜에서만).
 */
const BasePartsItemSchema = z.object({
  spec_category:    z.string(),
  option_part:      z.string().nullable(),
  is_unregistered:  z.boolean(),
  item_description: z.string().nullable(),
  description_mode: z.string(),
  per_server_qty:   z.number(),
});
const BasePartsResponseSchema = z.object({
  chassis_model_key: z.string(),
  /** 이 응답이 어느 컨디션의 기본 구성인가(NEW/USED). 마이그 173. */
  server_condition:  z.enum(['NEW', 'USED']),
  /** 저장된 기본 구성이 있는가. 빈 배열 저장(=삭제)과 구분한다. */
  exists:            z.boolean(),
  items:             z.array(BasePartsItemSchema),
});

export type BasePartsItem     = z.infer<typeof BasePartsItemSchema>;
export type BasePartsResponse = z.infer<typeof BasePartsResponseSchema>;

/**
 * 모델 키 + 컨디션의 기본 구성.
 * ★ 컨디션은 필수다 — 서버가 폴백하지 않는다(마이그 173). 중고에 저장된 것이
 *   없으면 `exists: false` 로 돌아오고, 화면은 비운 채로 시작한다.
 */
export function fetchBaseParts(
  modelKey:  string,
  condition: 'NEW' | 'USED',
): Promise<BasePartsResponse> {
  const qs = new URLSearchParams({ model_key: modelKey, condition }).toString();
  return getJson(
    `/api/admin/server-config/base-parts?${qs}`,
    BasePartsResponseSchema, 'GET /server-config/base-parts',
  );
}

/** 전체 교체. 빈 배열은 "기본 구성을 지운다"는 유효한 저장이다. */
export async function saveBaseParts(
  modelKey:  string,
  condition: 'NEW' | 'USED',
  items:     readonly BasePartsItem[],
): Promise<BasePartsResponse> {
  const res = await fetch(`${API_BASE}/api/admin/server-config/base-parts`, {
    method:  'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body:    JSON.stringify({ model_key: modelKey, condition, items }),
  });
  if (!res.ok) {
    throw await httpError(res);
  }
  return parseResponse(await res.json(), BasePartsResponseSchema, 'PUT /server-config/base-parts');
}

/** 서버 내부 부품 한 줄 입력. id 가 있으면 기존 자식행 갱신, 없으면 신규. */
export interface ServerChildInput {
  id?:                  string;
  spec_category:        string;
  option_part?:         string | null;
  item_description?:    string | null;
  /**
   * 위 문구의 출처 — 마이그 191. 미전송이면 서버가 'AUTO' 로 둔다.
   *   AUTO            카탈로그 스펙으로 만든 약식(수량이 바뀌면 다시 만든다)
   *   MANUAL_OVERRIDE 사람이 디스크립션 입력칸에 타이핑한 문구(다시 만들지 않는다)
   */
  description_mode?:    'AUTO' | 'MANUAL_OVERRIDE' | 'FREE_TEXT';
  quantity:             number;
  sell_unit_price_krw?: number | null;
  is_unregistered?:     boolean;
  notes?:               string | null;
  source_type?:         'stock' | 'deal_rfq' | 'rfq_history' | null;
  /** RFQ=rfq.id / 재고=inventory_stock.id. 둘 다 숫자 id 다. */
  source_ref_id?:       string | null;
  source_label?:        string | null;
  cost_basis_amount?:   number | null;
  cost_basis_currency?: string | null;
}

/** 서버 헤더 + 자식 전체 (생성/수정 공용). */
export interface ServerUpsertInput {
  configuration_id?:    string;
  server_condition:     'NEW' | 'USED';
  /**
   * 서버 대수는 **딜 소유**라 Config 편집기는 보내지 않는다 (`CONFIG2-018`).
   * 미전송이면 서버가 기존 값을 유지하고, 신규 생성은 1대로 시작한다.
   */
  server_quantity?:     number;
  /** 서버 1대 판매가(번들가). 미입력이면 null — 자식 합계를 자동 승격하지 않는다. */
  unit_price_override?: number | null;
  item_description?:    string | null;
  children:             ServerChildInput[];
}

/**
 * 인라인 patch 응답 — 바뀐 서브트리 + 갱신된 낙관적 잠금 버전.
 *
 * 복제·삭제는 딜 전체를 재조회하므로 version 이 필요 없지만, 인라인 편집은
 * 재조회 없이 연속으로 저장한다. version 을 안 받으면 두 번째 편집부터 409.
 */
const ServerPatchResultSchema = z.object({
  items:   z.array(DealItemSchema),
  version: z.number().int().positive(),
});
export type ServerPatchResult = z.infer<typeof ServerPatchResultSchema>;

async function sendServerPatch(
  url:   string,
  body:  unknown,
  label: string,
): Promise<ServerPatchResult> {
  const res = await fetch(url, {
    method:  'PATCH',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  });
  if (!res.ok) {
    const bDetail = errorDetail(await res.json().catch(() => null));
    if (res.status === 409) {
      throw new ApiConflictError(bDetail || '딜이 다른 곳에서 수정되었습니다.');
    }
    throw new Error(`HTTP ${res.status} ${bDetail || ''}`.trim());
  }
  return parseResponse(await res.json(), ServerPatchResultSchema, label);
}

async function sendServerCommand(
  url:    string,
  method: 'POST' | 'PUT' | 'DELETE',
  body:   unknown,
  label:  string,
): Promise<DealItem[]> {
  const res = await fetch(url, {
    method,
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  });
  if (!res.ok) {
    const bDetail = errorDetail(await res.json().catch(() => null));
    if (res.status === 409) {
      throw new ApiConflictError(bDetail || '딜이 다른 곳에서 수정되었습니다.');
    }
    throw new Error(`HTTP ${res.status} ${bDetail || ''}`.trim());
  }
  // DELETE 는 { ok: true } 만 돌려준다.
  if (method === 'DELETE') return [];
  return parseResponse(await res.json(), ServerItemsResponseSchema, label).items;
}

/** POST /deal/:dealId/server — 서버 추가 (편집기 신규 '완료'). */
export async function createDealServer(
  dealId:           string,
  input:            ServerUpsertInput,
  expectedVersion?: number,
): Promise<DealItem[]> {
  return sendServerCommand(
    `${API_BASE}/api/admin/deal/${encodeURIComponent(dealId)}/server`,
    'POST',
    { ...input, expected_version: expectedVersion },
    'POST /deal/:id/server',
  );
}

/** PUT /deal/server/:serverItemId — 서버 수정 (서브트리 diff 갱신). */
export async function updateDealServer(
  serverItemId:     string,
  input:            ServerUpsertInput,
  expectedVersion?: number,
): Promise<DealItem[]> {
  return sendServerCommand(
    `${API_BASE}/api/admin/deal/server/${encodeURIComponent(serverItemId)}`,
    'PUT',
    { ...input, expected_version: expectedVersion },
    'PUT /deal/server/:id',
  );
}

/**
 * PATCH /deal/server/:serverItemId/header — 대수·번들가만 (딜 상세 인라인).
 *
 * `updateDealServer`(PUT)를 쓰면 안 된다 — 그쪽은 자식 목록 전체를 받아 diff
 * 하는 계약이라, 헤더 두 값만 담아 부르면 구성품이 통째로 지워진다.
 */
export async function patchDealServerHeader(
  serverItemId: string,
  patch:        { server_quantity?: number; unit_price_override?: number | null },
  expectedVersion?: number,
): Promise<ServerPatchResult> {
  return sendServerPatch(
    `${API_BASE}/api/admin/deal/server/${encodeURIComponent(serverItemId)}/header`,
    { ...patch, expected_version: expectedVersion },
    'PATCH /deal/server/:id/header',
  );
}

/**
 * PATCH /deal/item/:itemId/source — 품목 소스·참고 판매가 (딜 상세 인라인).
 * 서버 구성품과 개별 품목 둘 다 대상이다.
 */
export async function patchDealItemSource(
  itemId: string,
  patch:  {
    sell_unit_price_krw?: number | null;
    source_type?:         'stock' | 'deal_rfq' | 'rfq_history' | null;
    source_ref_id?:       string | null;
    source_label?:        string | null;
    cost_basis_amount?:   number | null;
    cost_basis_currency?: string | null;
  },
  expectedVersion?: number,
): Promise<ServerPatchResult> {
  return sendServerPatch(
    `${API_BASE}/api/admin/deal/item/${encodeURIComponent(itemId)}/source`,
    { ...patch, expected_version: expectedVersion },
    'PATCH /deal/item/:id/source',
  );
}

/** POST /deal/server/:serverItemId/duplicate — 서버 복제 (원본 불변). */
export async function duplicateDealServer(
  serverItemId:     string,
  expectedVersion?: number,
): Promise<DealItem[]> {
  return sendServerCommand(
    `${API_BASE}/api/admin/deal/server/${encodeURIComponent(serverItemId)}/duplicate`,
    'POST',
    { expected_version: expectedVersion },
    'POST /deal/server/:id/duplicate',
  );
}

/** DELETE /deal/server/:serverItemId — 서버 삭제 (soft). */
export async function deleteDealServer(
  serverItemId:     string,
  expectedVersion?: number,
): Promise<void> {
  await sendServerCommand(
    `${API_BASE}/api/admin/deal/server/${encodeURIComponent(serverItemId)}`,
    'DELETE',
    { expected_version: expectedVersion },
    'DELETE /deal/server/:id',
  );
}
